import React, { useCallback, useMemo } from 'react';
import { Spinner } from '@heroui/react';
import { Typography, UniversalTable } from '../../../../components';
import { EmptyContentClientIc } from '../../../../components/icons';
import { StatisticsTableProps, StatisticsData } from '../types';
import { getTableColumns } from '../constants';
import { useTranslations } from '../../../../contexts/translation.context';
import { StatisticPeriodCell, StatisticSubscribersCell, StatisticIncomeCell } from './index';

const StatisticsTable: React.FC<StatisticsTableProps> = ({
  statistics,
  isLoading,
  totalCount,
  currentPage,
  pageSize,
  onPageChange,
  onPageSizeChange,
}) => {
  const { t } = useTranslations();
  const columns = useMemo(() => getTableColumns(t), [t]);

  const renderCell = useCallback((statistic: StatisticsData, columnKey: string) => {
    switch (columnKey) {
      case 'period':
        return <StatisticPeriodCell statistic={statistic} />;
      case 'subscribers':
        return <StatisticSubscribersCell statistic={statistic} />;
      case 'income':
        return <StatisticIncomeCell statistic={statistic} />;
      default:
        return null;
    }
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <UniversalTable
      mobileSize="sm"
      data={statistics}
      columns={columns}
      renderCell={renderCell}
      emptyContent={
        <div className="text-center py-12">
          <div className="flex flex-col items-center gap-4">
            <EmptyContentClientIc />
            <div className="flex flex-col gap-1.5">
              <Typography variant="h6" className="text-xl font-semibold leading-7">
                {t('statistics.table.empty.title')}
              </Typography>
              <Typography
                className="text-neutral-500 text-base font-medium leading-6"
                variant="body1"
              >
                {t('statistics.table.empty.description')}
              </Typography>
            </div>
          </div>
        </div>
      }
      pagination={{
        count: totalCount,
        page_size: pageSize,
        page: currentPage,
      }}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
      currentPage={currentPage}
      currentPageSize={pageSize}
    />
  );
};

export default StatisticsTable;
